<template>
    <v-container fluid class="pa-6">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span id="title" class="page-title">Documentos solicitados</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Homepage', disabled: false, to: '/home', exact: true },
                            { text: 'Documentos Solicitados', disabled: true, exact: true }
                        ]"></v-breadcrumbs>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card flat class="filter-card">
                    <v-card-text class="pa-5">
                        <v-form @submit.prevent="applyFilter()">
                            <v-row>
                                <v-col cols="12" md="5">
                                    <v-text-field dense label="Credor (nome ou CPF/CNPJ)" v-model="filter.creditor" hide-details outlined clearable></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select dense :items="classOptions" label="Classe" v-model="filter.classId" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select dense :items="statusOptions" label="Status" v-model="filter.status" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-btn type="submit" color="primary" dark elevation="0"><v-icon left small>mdi-magnify</v-icon>Filtrar</v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-title class="table-card-title">
                        Documentos solicitados
                        <v-spacer></v-spacer>
                        <span class="text-caption grey--text text--darken-1">{{ pendingCount }} solicitação(ões) pendente(s)</span>
                    </v-card-title>
                    <v-card-text>
                        <v-data-table :headers="headers" :items="filteredRequests" :loading="loading"
                            :disable-sort="true" no-data-text="Nenhuma solicitação de documentos encontrada"
                            loading-text="Carregando dados..." class="app-table hover-table"
                            :footer-props="footerOptions" :items-per-page="20">

                            <template v-slot:[`item.creditor`]="{ item }">
                                <div class="py-2">
                                    <span class="list-item-title">{{ item.creditor_name || 'Solicitação geral do caso' }}</span><br />
                                    <span v-if="item.creditor_registration" class="text-caption grey--text text--darken-1">{{ formatRegistration(item.creditor_registration) }}</span>
                                    <span v-else class="text-caption grey--text text--darken-1">Solicitação #{{ item.id }}</span>
                                </div>
                            </template>

                            <template v-slot:[`item.credit`]="{ item }">
                                <div v-if="item.credit && (item.credit.class_name || item.credit.amount !== null)" class="py-2">
                                    <span class="font-weight-medium">{{ item.credit.class_name || 'Classe não informada' }}</span><br />
                                    <span class="text-caption grey--text text--darken-1">
                                        <template v-if="item.credit.amount !== null && item.credit.amount !== undefined">{{ formatAmount(item.credit.amount, item.credit.currency) }}</template>
                                        <template v-if="item.credit.company_name"> · {{ item.credit.company_name }}</template>
                                    </span>
                                </div>
                                <span v-else class="grey--text text-caption">—</span>
                            </template>

                            <template v-slot:[`item.documents`]="{ item }">
                                <div class="py-2">
                                    <strong>{{ sentCount(item) }} de {{ item.documents.length }}</strong> enviado(s)
                                    <div v-for="doc in item.documents" :key="doc.id" class="text-caption d-flex align-center">
                                        <v-icon x-small class="mr-1" :color="isDocSent(doc) ? 'success' : 'grey'">
                                            {{ isDocSent(doc) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                        </v-icon>
                                        {{ doc.name }}
                                        <span v-if="fileCount(doc) > 1" class="grey--text ml-1">({{ fileCount(doc) }} arquivos)</span>
                                    </div>
                                </div>
                            </template>

                            <template v-slot:[`item.deadline`]="{ item }">
                                <span :class="isExpired(item.deadline) && !isComplete(item) ? 'error--text font-weight-bold' : ''">
                                    {{ formatDate(item.deadline) }}
                                </span>
                            </template>

                            <template v-slot:[`item.status`]="{ item }">
                                <v-chip :color="requestStatus(item).color" label small text-color="white">
                                    {{ requestStatus(item).name }}
                                </v-chip>
                            </template>

                            <template v-slot:[`item.actions`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon color="primary" v-bind="attrs" v-on="on" @click="openUploadModal(item)">
                                                <v-icon>{{ canUpload(item) ? 'mdi-upload-outline' : 'mdi-file-document-multiple-outline' }}</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ canUpload(item) ? 'Enviar documentos' : 'Ver documentos' }}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon color="error" v-bind="attrs" v-on="on" :disabled="isComplete(item)" @click="openJustifyModal(item)">
                                                <v-icon>mdi-alert-circle-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.justification ? 'Justificativa enviada' : 'Justificar ausência de envio' }}</span>
                                    </v-tooltip>
                                </div>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="dialogUpload" max-width="800px" persistent scrollable>
            <v-card v-if="selectedRequest">
                <v-card-title class="headline primary white--text">
                    <div>
                        <div>{{ canUpload(selectedRequest) ? 'Enviar documentos' : 'Documentos solicitados' }}</div>
                        <div class="text-subtitle-2 font-weight-regular">
                            {{ selectedRequest.creditor_name || 'Solicitação geral do caso' }}
                            <span v-if="selectedRequest.creditor_registration"> · {{ formatRegistration(selectedRequest.creditor_registration) }}</span>
                        </div>
                        <div v-if="selectedRequest.credit && selectedRequest.credit.class_name" class="text-caption font-weight-regular">
                            {{ selectedRequest.credit.class_name }}
                            <template v-if="selectedRequest.credit.amount !== null && selectedRequest.credit.amount !== undefined"> · {{ formatAmount(selectedRequest.credit.amount, selectedRequest.credit.currency) }}</template>
                            <template v-if="selectedRequest.credit.company_name"> · {{ selectedRequest.credit.company_name }}</template>
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="dialogUpload = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-5">
                    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                        <v-chip small label :color="requestStatus(selectedRequest).color" text-color="white">{{ requestStatus(selectedRequest).name }}</v-chip>
                        <span class="text-body-2">Prazo para envio: <strong :class="isExpired(selectedRequest.deadline) ? 'error--text' : ''">{{ formatDate(selectedRequest.deadline) }}</strong></span>
                        <span class="text-body-2 grey--text text--darken-1">· Solicitado em {{ formatDate(selectedRequest.created_at) }}</span>
                    </div>

                    <v-alert v-if="selectedRequest.message" type="info" text dense class="mb-4">
                        <strong>Orientações da Administração Judicial:</strong>
                        <div class="request-message">{{ selectedRequest.message }}</div>
                    </v-alert>

                    <v-alert v-if="isExpired(selectedRequest.deadline) && !isComplete(selectedRequest)" type="error" text dense class="mb-4">
                        O prazo de envio foi encerrado. Se não foi possível enviar os documentos, registre uma justificativa.
                    </v-alert>
                    <p v-else-if="canUpload(selectedRequest)" class="mb-4 text-body-2">
                        Selecione um ou mais arquivos para cada documento (PDF, PNG ou JPG). O envio acontece na hora, ao escolher os arquivos.
                    </p>

                    <v-list class="pa-0">
                        <v-list-item v-for="doc in selectedRequest.documents" :key="doc.id" class="pa-3 mb-2 rounded doc-item">
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold">{{ doc.name }}</v-list-item-title>
                                <v-list-item-subtitle class="mt-1">
                                    <v-chip x-small :color="doc.uploaded_after_deadline ? 'error' : (isDocSent(doc) ? 'success' : (isExpired(selectedRequest.deadline) ? 'error' : 'warning'))" dark>
                                        {{ doc.tag || (isDocSent(doc) ? 'Enviado' : 'Pendente') }}
                                    </v-chip>
                                    <span v-if="fileCount(doc)" class="ml-2 text-caption">{{ fileCount(doc) }} arquivo(s)</span>
                                </v-list-item-subtitle>
                                <div v-for="file in (doc.files || [])" :key="`file-${doc.id}-${file.id}`" class="text-caption d-flex align-center mt-1">
                                    <a :href="file.url" target="_blank" class="mr-2"><v-icon x-small color="primary">mdi-file-outline</v-icon> {{ file.name }}</a>
                                    <span class="grey--text">{{ formatDateTime(file.uploaded_at) }}</span>
                                    <v-btn v-if="canUpload(selectedRequest) && file.id" icon x-small color="error" class="ml-1" title="Remover arquivo"
                                        :disabled="doc.isUploading" @click="removeFile(doc, file)">
                                        <v-icon x-small>mdi-close</v-icon>
                                    </v-btn>
                                </div>
                                <div v-if="doc.isUploading && doc.uploadTotal > 1" class="text-caption primary--text mt-1">
                                    Enviando {{ doc.uploadDone }} de {{ doc.uploadTotal }}...
                                </div>
                            </v-list-item-content>

                            <v-list-item-action>
                                <input type="file" multiple :ref="'fileInput_' + doc.id" class="d-none" @change="onFilesSelected(doc, $event)" accept=".pdf,.png,.jpg,.jpeg" />

                                <v-btn v-if="canUpload(selectedRequest)" :color="isDocSent(doc) ? 'primary' : 'primary'" :outlined="isDocSent(doc)" small elevation="0" :loading="doc.isUploading" @click="triggerFileInput(doc.id)">
                                    <v-icon left small>mdi-upload-outline</v-icon> {{ isDocSent(doc) ? 'Adicionar arquivo' : 'Selecionar e enviar' }}
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-btn v-if="!isComplete(selectedRequest)" color="error" text @click="openJustifyModal(selectedRequest)">
                        <v-icon left>mdi-alert-circle-outline</v-icon> Justificativa
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="dialogUpload = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogJustify" max-width="800px" persistent>
            <v-card v-if="selectedRequest">
                <v-card-title class="headline error white--text">
                    <div>
                        <div>Justificar ausência de envio</div>
                        <div class="text-subtitle-2 font-weight-regular">{{ selectedRequest.creditor_name || 'Solicitação geral do caso' }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="dialogJustify = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-5">
                    <v-alert type="info" text dense class="mb-4">
                        Descreva abaixo o motivo pelo qual os documentos solicitados não puderam ser enviados dentro do prazo.
                    </v-alert>

                    <tiptap-vuetify v-model="justificationText" :extensions="extensions" placeholder="Escreva sua justificativa aqui..." />
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="dialogJustify = false" :disabled="justifyLoading">Cancelar</v-btn>
                    <v-btn color="error" elevation="0" @click="sendJustification()" :loading="justifyLoading">
                        <v-icon left>mdi-send</v-icon> Enviar Justificativa
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import axios from 'axios';
import { TiptapVuetify, Bold, Italic, Strike, Underline, Paragraph } from 'tiptap-vuetify';
import currencyTypes from '@/common/data/currency-types.data';

const STATUS = {
    pending: { id: 'pending', name: 'Pendente', color: 'warning' },
    overdue: { id: 'overdue', name: 'Atrasado', color: 'error' },
    sent: { id: 'sent', name: 'Enviado', color: 'success' },
    justified: { id: 'justified', name: 'Justificado', color: 'accent' },
};

export default {
    name: "ClientDocument",

    components: {
        'tiptap-vuetify': TiptapVuetify,
    },

    data: () => ({
        loading: false,
        requests: [],

        filter: { creditor: '', status: '', classId: '' },
        appliedFilter: { creditor: '', status: '', classId: '' },
        statusOptions: [
            { id: '', name: 'Todos' },
            { id: 'pending', name: 'Pendente' },
            { id: 'overdue', name: 'Atrasado' },
            { id: 'sent', name: 'Enviado' },
            { id: 'justified', name: 'Justificado' },
        ],

        headers: [
            { text: 'Credor', value: 'creditor' },
            { text: 'Crédito', value: 'credit' },
            { text: 'Documentos', value: 'documents' },
            { text: 'Prazo', value: 'deadline' },
            { text: 'Status', value: 'status' },
            { text: 'Ações', value: 'actions', sortable: false },
        ],
        footerOptions: {
            itemsPerPageText: 'Itens por página:',
            itemsPerPageOptions: [10, 20, 50, 100],
        },

        dialogUpload: false,
        dialogJustify: false,
        selectedRequest: null,

        justificationText: '',
        justifyLoading: false,

        extensions: [Underline, Strike, Italic, Bold, Paragraph],
    }),

    computed: {
        // Classes presentes nas solicitacoes, na ordem em que a API devolveu (ordem das classes do caso).
        classOptions() {
            const seen = new Map();
            this.requests.forEach((request) => {
                const credit = request.credit || {};
                if (!credit.class_id || seen.has(credit.class_id)) return;
                seen.set(credit.class_id, { id: credit.class_id, name: credit.class_name || `Classe ${credit.class_id}` });
            });
            return [{ id: '', name: 'Todas' }, ...seen.values()];
        },

        filteredRequests() {
            const term = String(this.appliedFilter.creditor || '').trim().toLowerCase();
            const digits = term.replace(/\D/g, '');
            const status = this.appliedFilter.status;
            const classId = this.appliedFilter.classId;

            return this.requests.filter((request) => {
                if (status && this.requestStatus(request).id !== status) return false;
                if (classId && !(request.credit && Number(request.credit.class_id) === Number(classId))) return false;
                if (!term) return true;

                const name = String(request.creditor_name || '').toLowerCase();
                const registration = String(request.creditor_registration || '');
                return name.includes(term) || (digits && registration.includes(digits));
            });
        },

        pendingCount() {
            return this.requests.filter((request) => !this.isComplete(request)).length;
        },
    },

    created() {
        this.loadRequests();
    },

    methods: {
        applyFilter() {
            this.appliedFilter = { ...this.filter };
        },

        async loadRequests() {
            this.loading = true;
            try {
                const result = await axios.get('/api/client/document-requests');

                const data = result.data.content || result.data;
                // A API ja devolve ordenado por classe e nome do credor.
                this.requests = data.map((req) => ({
                    ...req,
                    documents: (req.documents || []).map((doc) => ({ ...doc, files: doc.files || [], isUploading: false, uploadDone: 0, uploadTotal: 0 })),
                }));

                // Mantem o dialogo aberto apontando para a solicitacao recarregada.
                if (this.selectedRequest) {
                    const refreshed = this.requests.find((request) => request.id === this.selectedRequest.id);
                    if (refreshed) this.selectedRequest = refreshed;
                }
            } catch (error) {
                this.$dialog.notify.error("Erro ao carregar solicitações de documentos.", { position: 'top-right' });
            } finally {
                this.loading = false;
            }
        },

        fileCount(doc) {
            return doc && Array.isArray(doc.files) ? doc.files.length : 0;
        },

        isDocSent(doc) {
            return !!(doc && (this.fileCount(doc) || doc.file_url || Number(doc.status) === 1));
        },

        sentCount(request) {
            return (request.documents || []).filter((doc) => this.isDocSent(doc)).length;
        },

        isComplete(request) {
            const docs = request.documents || [];
            return docs.length > 0 && docs.every((doc) => this.isDocSent(doc));
        },

        canUpload(request) {
            return !this.isExpired(request.deadline);
        },

        requestStatus(request) {
            if (this.isComplete(request)) return STATUS.sent;
            if (request.justification) return STATUS.justified;
            if (this.isExpired(request.deadline)) return STATUS.overdue;
            return STATUS.pending;
        },

        formatRegistration(value) {
            const digits = String(value || '').replace(/\D/g, '');
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value;
        },

        formatAmount(amount, currency) {
            const value = Number(amount || 0);
            const type = currencyTypes.find((item) => Number(item.id) === Number(currency));
            const symbol = type ? type.currencyFormat : 'R$';
            return `${symbol} ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        isExpired(deadline) {
            if (!deadline) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const limitDate = new Date(deadline);
            limitDate.setHours(0, 0, 0, 0);

            return today > limitDate;
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            const userTimezoneOffset = date.getTimezoneOffset() * 60000;
            const correctedDate = new Date(date.getTime() + userTimezoneOffset);
            return correctedDate.toLocaleDateString('pt-BR');
        },

        formatDateTime(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return '';
            return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        },

        openUploadModal(request) {
            this.selectedRequest = request;
            this.dialogUpload = true;
        },

        triggerFileInput(docId) {
            const input = this.$refs['fileInput_' + docId];
            if (input && input[0]) {
                input[0].value = '';
                input[0].click();
            }
        },

        // Envio imediato ao escolher; varios arquivos seguem em sequencia.
        async onFilesSelected(doc, event) {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            this.$set(doc, 'isUploading', true);
            this.$set(doc, 'uploadTotal', files.length);
            this.$set(doc, 'uploadDone', 0);

            let sent = 0;
            try {
                for (const file of files) {
                    const formData = new FormData();
                    formData.append('file', file);

                    try {
                        await axios.post(`/api/client/document-requests/${this.selectedRequest.id}/documents/${doc.id}/upload`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        sent += 1;
                    } catch (error) {
                        this.$dialog.notify.error(`Falha ao enviar "${file.name}": ${this.$codes(error) || 'erro inesperado'}`, { position: 'top-right', timeout: 5000 });
                    }
                    this.$set(doc, 'uploadDone', doc.uploadDone + 1);
                }

                if (sent) {
                    this.$dialog.notify.info(sent === 1 ? `"${doc.name}" enviado com sucesso!` : `${sent} arquivo(s) enviado(s) para "${doc.name}".`, { position: "top-right", timeout: 2500 });
                    await this.loadRequests();
                }
            } finally {
                this.$set(doc, 'isUploading', false);
                this.$set(doc, 'uploadTotal', 0);
            }
        },

        async removeFile(doc, file) {
            const proceed = await this.$dialog.confirm({
                title: 'Remover arquivo',
                text: `Remover o arquivo "${file.name}" de "${doc.name}"?`,
                actions: { false: 'Cancelar', true: 'Remover' },
            });
            if (!proceed) return;

            this.$set(doc, 'isUploading', true);
            try {
                await axios.delete(`/api/client/document-requests/${this.selectedRequest.id}/documents/${doc.id}/files/${file.id}`);
                this.$dialog.notify.info('Arquivo removido.', { position: 'top-right', timeout: 2000 });
                await this.loadRequests();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao remover o arquivo.', { position: 'top-right' });
            } finally {
                this.$set(doc, 'isUploading', false);
            }
        },

        openJustifyModal(request) {
            this.selectedRequest = request;
            this.justificationText = request.justification || '';
            this.dialogJustify = true;
        },

        async sendJustification() {
            if (!this.justificationText || this.justificationText.trim() === '<p></p>') {
                this.$dialog.notify.warning("A justificativa não pode estar vazia.", { position: 'top-right' });
                return;
            }

            this.justifyLoading = true;

            try {
                await axios.post(`/api/client/document-requests/${this.selectedRequest.id}/justify`, {
                    justification: this.justificationText
                });

                this.$dialog.notify.info("Justificativa enviada com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogJustify = false;

                await this.loadRequests();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || "Erro ao enviar justificativa.", { position: 'top-right' });
            } finally {
                this.justifyLoading = false;
            }
        },
    }
};
</script>

<style scoped>
.doc-item {
    border: 1px solid #eee;
}

.request-message {
    white-space: pre-line;
}
</style>
